<template>
    <div>
        <transition appear name="fade" mode="out-in">
            <div class="card">
                <div v-if="hasChartData" class="charts" ref="lineChartContainer">
                    <canvas ref="lineCanvas"></canvas>
                </div>
                <h1 v-else class="no-data">
                    No data for those filters
                </h1>
            </div>
        </transition>
    </div>
</template>

<script>
    import Moment from 'moment';

    import Chart from 'chart.js';
    import store from '@/store';
    import { colors } from '@/util/config';

    export default {
        props: {
            filters: {
                type: Object,
            },
            views: {
                type: Object,
            },
            series: {
                type: Array,
            },
        },
        data() {
            return {
                lineChart: null,
                uniqueCategories: [],
                uniqueCategoryValues: {},
                hasChartData: true,
                leftLabel: '',
            };
        },
        watch: {
            filters: {
                handler() {
                    this.updateChart();
                },
                deep: true,
            },
            views: {
                handler() {
                    this.updateChart();
                },
                deep: true,
            },
            series() {
                this.updateChart();
            },
            chartData() {
                this.updateChart();
            },
        },
        computed: {
            chartData() {
                return store.state.chartData;
            },
        },
        methods: {
            calculate() {
                const tempData = {};
                const seriesData = {};

                this.uniqueCategories = [];
                this.uniqueCategoryValues = {};

                // clone array. not robust. methods and Infinity will be stripped
                let filteredData = JSON.parse(JSON.stringify(this.chartData.data));


                if (Object.keys(this.filters).length) {
                    Object.keys(this.filters).forEach((filterCategory) => {
                        const filterValue = this.filters[filterCategory];

                        if (filterValue) {
                            filteredData = filteredData.filter(datum => (
                                datum.categories[filterCategory] === filterValue
                            ));
                        }
                    });
                }

                filteredData.forEach((datum, filteredIndex) => {
                    const date = datum.categories.Date;

                    if (this.views.dateGroup === 'Weeks') {
                        filteredData[filteredIndex].categories.Date = Moment(date, 'YYYY-MM-DD').format('W YYYY');
                    } else if (this.views.dateGroup === 'Months') {
                        filteredData[filteredIndex].categories.Date = Moment(date, 'YYYY-MM-DD').format('MMMM YYYY');
                    }

                    Object.keys(datum.categories).forEach((category) => {
                        if (!this.uniqueCategories.includes(category)) {
                            this.uniqueCategories.push(category);
                        }

                        if (!Object.keys(this.uniqueCategoryValues).includes(category)) {
                            this.uniqueCategoryValues[category] = [datum.categories[category]];
                            // eslint-disable-next-line
                        } else if (!this.uniqueCategoryValues[category].includes(datum.categories[category])) {
                            this.uniqueCategoryValues[category].push(datum.categories[category]);
                        }
                    });
                });

                this.uniqueCategoryValues[this.views.XValue].forEach((d) => {
                    tempData[d] = {};
                    this.series.forEach((s) => {
                        tempData[d][s] = 0;
                    });
                });

                const uniqueValueNames = [];

                filteredData.forEach((datum) => {
                    Object.keys(datum.values).forEach((v) => {
                        const keyName = datum.categories[this.views.XValue];

                        tempData[keyName][v] += datum.values[v];

                        if (!uniqueValueNames.includes(v)) {
                            uniqueValueNames.push(v);
                        }
                    });
                });

                uniqueValueNames.push('CTR %');

                uniqueValueNames.forEach((v) => {
                    seriesData[v] = [];

                    Object.keys(tempData).forEach((date) => {
                        const tempDatum = tempData[date];
                        const y = v === 'CTR %' ? (tempDatum.Clicks / tempDatum.Impressions) * 100 : tempDatum[v];

                        seriesData[v].push({
                            x: date,
                            y,
                        });
                    });
                });

                return seriesData;
            },
            updateChart() {
                if (this.views.XValue === null) {
                    return;
                }

                this.createChart();

                const plotData = this.calculate();

                const availableSeries = Object.keys(plotData);
                // Remove all datasets
                this.lineChart.data.datasets = [];

                const chartData = {};

                if (this.views.Series) {
                    this.leftLabel = this.views.Series;
                }

                this.lineChart.options
                    .scales.yAxes[0].scaleLabel.labelString = this.views.Series === null ?
                        'Impressions/Clicks' :
                        this.leftLabel;

                if (this.views.Series !== null) {
                    this.lineChart.options
                        .scales.yAxes[1].display = false;
                } else {
                    this.lineChart.options
                        .scales.yAxes[1].display = true;
                }

                availableSeries.forEach((s) => {
                    chartData[s] = {
                        label: s,
                        data: plotData[s],
                        fill: 'origin',
                        borderColor: colors[s].line,
                        lineTension: 0.1,
                        pointRadius: 0,
                        pointHitRadius: 15,
                        tooltips: {
                            backgroundColor: colors[s].backgroundColor,
                        },
                        backgroundColor: colors[s].backgroundColor,
                        yAxisID: s === 'CTR %' ? 'yRight' : 'yLeft',
                        pointHoverBackgroundColor: colors[s].line,
                        pointHoverBorderColor: colors[s].line,
                    };

                    if (this.views.XValue !== 'Date') {
                        chartData[s].backgroundColor = colors[s].backgroundColor;
                    }
                });

                Object.keys(plotData).forEach((s) => {
                    if (!this.views.Series) {
                        this.lineChart.data.datasets.push(chartData[s]);
                    } else if (this.views.Series && s === this.views.Series) {
                        this.lineChart.data.datasets.push(chartData[s]);
                    }
                });

                this.lineChart.data.datasets.reverse();

                if (this.views.XValue !== 'Date' || this.views.dateGroup !== 'Days') {
                    const xAxes = this.lineChart.options.scales.xAxes[0];
                    xAxes.type = 'category';
                    xAxes.labels = this.uniqueCategoryValues[this.views.XValue];
                } else {
                    const xAxes = this.lineChart.options.scales.xAxes[0];
                    xAxes.type = 'time';
                    delete xAxes.labels;
                }

                this.lineChart.update();
            },
            createChart() {
                if (this.lineChart) {
                    this.lineChart.destroy();
                    this.lineChart = null;
                }

                if (this.views.Series) {
                    this.leftLabel = this.views.Series;
                }

                const options = {
                    legend: {
                        position: 'bottom',
                    },
                    scales: {
                        xAxes: [
                            {
                                type: this.views.XValue === 'Date' ? 'time' : 'category',
                                distribution: 'linear',
                            },
                        ],
                        yAxes: [
                            {
                                id: 'yLeft',
                                stacked: false,
                                ticks: {
                                    callback(value) {
                                        return value.toLocaleString('en-GB', { useGrouping: true });
                                    },
                                },
                                scaleLabel: {
                                    display: true,
                                    labelString: this.leftLabel === null ? 'Impressions/Clicks' : this.leftLabel,
                                },
                            },
                            {
                                id: 'yRight',
                                stacked: false,
                                ticks: {
                                    callback(value) {
                                        return value.toLocaleString('en-GB', { maximumFractionDigits: 2 });
                                    },
                                },
                                position: 'right',
                                scaleLabel: {
                                    display: true,
                                    labelString: 'CTR %',
                                },
                            },
                        ],
                    },
                    tooltips: {
                        cornerRadius: 0,
                        displayColors: true,
                        mode: 'index',
                        position: 'nearest',
                        intersect: true,
                        xPadding: 16,
                        yPadding: 16,
                        backgroundColor: '#ffffff',
                        borderColor: colors.fonts.kpmgBlue,
                        borderWidth: 1,
                        titleFontColor: colors.fonts.kpmgBlue,
                        bodyFontColor: colors.fonts.kpmgBlue,
                        bodySpacing: 4,
                        callbacks: {
                            label(item, data) {
                                const label = data.datasets[item.datasetIndex].label;
                                const value = item.yLabel;

                                const format = {
                                    useGrouping: true,
                                    maximumFractionDigits: 2,
                                };

                                if (value >= 1) {
                                    format.maximumFractionDigits = 0;
                                }

                                const formattedValue = value.toLocaleString('en-GB', format);


                                return `${label}: ${formattedValue}`;
                            },
                            labelColor(tooltipItem, chart) {
                                const itemIndex = tooltipItem.datasetIndex;
                                const dataset = chart.config.data.datasets[itemIndex];

                                return {
                                    borderColor: 'transparent',
                                    backgroundColor: dataset.borderColor,
                                };
                            },
                        },
                    },
                };

                if (this.views.XValue === 'Date') {
                    options.scales.xAxes[0].time = {
                        unit: 'day',
                        displayFormats: { day: 'DD MMM YYYY' },
                        parser: 'YYYY-MM-DD',
                    };
                }

                const config = {
                    type: this.views.XValue === 'Date' ? 'line' : 'bar',
                    data: {
                        datasets: [],
                    },
                    options,
                };

                this.lineChart = new Chart(this.$refs.lineCanvas, config);
            },
        },
        mounted() {
            this.createChart();
            this.updateChart();
        },
    };
</script>

<style lang="scss" scoped>
    @import '../../assets/scss/partials/variables';

    h3 {
        margin-bottom: 42px;
        font-size: 35px;
        color: $kpmg-blue;

        @media screen and (min-width: $tablet) {
            margin-bottom: 35px;
        }
    }

    .no-data {
        margin-bottom: 43px;
        color: $kpmg-blue;
        text-align: center;
    }

    .charts {
        max-width: 1380px;
        margin: 0 auto;
        padding-right: $mobile-side-gutter;
    }
</style>



// WEBPACK FOOTER //
// src/components/tiles/LineChart.vue